import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  FormLabel,
  Button,
  ButtonGroup,
  Autocomplete,
  FormControl,
  Checkbox,
  Fade,
} from "@mui/material";
import Swal from "sweetalert2";
import {
  CommonSubHeader,
  useLocationHelpers,
} from "../../Components/helpers/Helpers";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../api/axios";

const Editar = () => {
  const navigate = useNavigate();
  const { goView, goTable } = useLocationHelpers();
  const [searchParams] = useSearchParams();
  const associationId = searchParams.get("id");

  // --- Estados agrupados ---
  const [options, setOptions] = useState({
    companies: [],
    provinces: [],
    groups: [],
    notifications: [],
    agreementSigned: [],
  });

  const [loadingOptions, setLoadingOptions] = useState({
    companies: false,
    provinces: false,
    groups: false,
    notifications: false,
    agreementSigned: false,
  });

  const [loading, setLoading] = useState(false);

  const [associationData, setAssociationData] = useState({
    associationCompany: "",
    associationName: "",
    associationGroups: null,
    associationNotifications: null,
    associationContactPerson: "",
    associationCif: "",
    associationAddress: "",
    associationProvince: null,
    associationPostalCode: "",
    associationEmail: "",
    associationAgreementSigned: null,
    associationPhone: "",
  });

  const [associationObservations, setAssociationObservations] = useState("");
  const [marketingToSend, setMarketingToSend] = useState(0);
  const [marketingToShow, setMarketingToShow] = useState(false);

  // --- Funciones utilitarias ---
  const alertError = (title, text) =>
    Swal.fire({ title, icon: "error", text, confirmButtonColor: "#1ab394" });

  const alertConfirm = async (options) => {
    const defaultOptions = {
      showDenyButton: true,
      confirmButtonColor: "#1ab394",
      cancelButtonColor: "#d33",
      reverseButtons: true,
      confirmButtonText: "Guardar",
      denyButtonText: "Volver",
    };
    return await Swal.fire({ ...defaultOptions, ...options });
  };

  const findOption = (list, id) =>
    list.find((option) => option.id === id) || list[0];

  // --- Obtener datos ---
  const getData = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(
        `${process.env.REACT_APP_API_URL}/associations/edit`,
        { id: associationId },
      );

      const assoc = data.assoc?.[0] || {};
      const optionsList = [
        { nombre: "Seleccionar", id: -1 },
        { nombre: "Si", id: 1 },
        { nombre: "No", id: 0 },
      ];

      setMarketingToShow(assoc.marketing !== 0);
      setMarketingToSend(assoc.marketing !== 0 ? 1 : 0);

      setAssociationObservations(
        assoc.asoc_observaciones?.replace(/<br \/>/g, "\n") || "",
      );

      setAssociationData({
        associationCompany: assoc.company_relation,
        associationName: assoc.asoc_name,
        associationGroups: assoc.group_relations,
        associationNotifications: findOption(
          optionsList,
          assoc.asoc_notifications,
        ),
        associationContactPerson: assoc.asoc_contactname,
        associationCif: assoc.asoc_cif,
        associationAddress: assoc.asoc_address,
        associationProvince: assoc.provinces_relation,
        associationPostalCode: assoc.asoc_postalcode,
        associationEmail: assoc.asoc_email,
        associationAgreementSigned: findOption(optionsList, assoc.asoc_acuerdo),
        associationPhone: assoc.asoc_phone,
      });

      setOptions({
        companies: [{ company_name: "Seleccionar", id: 0 }, ...data.companies],
        provinces: [{ provincia: "Seleccionar", id: 0 }, ...data.provinces],
        groups: [{ grupo: "Seleccionar", id: 0 }, ...data.groups],
        notifications: optionsList,
        agreementSigned: optionsList,
      });
    } catch (error) {
      console.error(error);
      alertError("Error", "No se pudieron obtener los datos de la asociación.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
  }, []);

  // --- Configuración de campos ---
  const formFields = [
    {
      label: "Empresa",
      name: "associationCompany",
      type: "autocomplete",
      options: options.companies,
      loading: loadingOptions.companies,
      isOptionEqualToValue: (opt, val) =>
        opt.company_name === val?.company_name,
      getOptionLabel: (opt) => opt.company_name || "",
    },
    {
      label: "Provincia",
      name: "associationProvince",
      type: "autocomplete",
      options: options.provinces,
      loading: loadingOptions.provinces,
      isOptionEqualToValue: (opt, val) => opt.provincia === val?.provincia,
      getOptionLabel: (opt) => opt.provincia || "",
    },
    {
      label: "Grupos",
      name: "associationGroups",
      type: "autocomplete",
      options: options.groups,
      loading: loadingOptions.groups,
      isOptionEqualToValue: (opt, val) => opt.grupo === val?.grupo,
      getOptionLabel: (opt) => opt.grupo || "",
    },
    {
      label: "¿Los clientes reciben notificaciones?",
      name: "associationNotifications",
      type: "autocomplete",
      options: options.notifications,
      loading: loadingOptions.notifications,
      isOptionEqualToValue: (opt, val) => opt.nombre === val?.nombre,
      getOptionLabel: (opt) => opt.nombre || "",
    },
    {
      label: "¿Acuerdo firmado?",
      name: "associationAgreementSigned",
      type: "autocomplete",
      options: options.agreementSigned,
      loading: loadingOptions.agreementSigned,
      isOptionEqualToValue: (opt, val) => opt.nombre === val?.nombre,
      getOptionLabel: (opt) => opt.nombre || "",
    },
    { name: "associationName", label: "Nombre", type: "text" },
    { name: "associationPhone", label: "Teléfono", type: "text" },
    {
      name: "associationContactPerson",
      label: "Persona de contacto",
      type: "text",
    },
    { name: "associationCif", label: "CIF", type: "text" },
    { name: "associationAddress", label: "Dirección", type: "text" },
    { name: "associationPostalCode", label: "Código postal", type: "text" },
    { name: "associationEmail", label: "Email", type: "text" },
    {
      name: "associationObservations",
      label: "Observaciones",
      type: "textarea",
    },
  ];

  // --- Validación ---
  const isFilled = (val, fieldName) => {
    if (val == null) return false; // null o undefined
    if (typeof val === "string") return val.trim() !== "";
    if (Array.isArray(val)) return val.length > 0;
    if (typeof val === "object") {
      // Si el objeto tiene un id (Autocomplete típico), consideramos vacío cuando id === 0 o id == null
      if ("id" in val) {
        return val.id !== 0 && val.id !== null && val.id !== undefined;
      }
      // Si tiene propiedades textuales (provincia, company_name, grupo, nombre), verificamos que alguna tenga texto válido
      const textualKeys = ["provincia", "company_name", "grupo", "nombre"];
      for (const k of textualKeys) {
        if (
          k in val &&
          typeof val[k] === "string" &&
          val[k].trim() !== "" &&
          val[k].trim().toLowerCase() !== "seleccionar"
        ) {
          return true;
        }
      }
      // Por defecto, si tiene alguna key con valor no vacío, lo consideramos lleno
      return Object.keys(val).some((k) => {
        const v = val[k];
        return v != null && !(typeof v === "string" && v.trim() === "");
      });
    }
    // números y booleanos los consideramos válidos
    return true;
  };

  const requiredFieldNames = [
    "associationCompany",
    "associationName",
    "associationGroups",
    "associationProvince",
    "associationPostalCode",
    "associationContactPerson",
    "associationEmail",
    "associationPhone",
  ];

  const validateFields = () => {
    return requiredFieldNames.every((name) =>
      isFilled(associationData[name], name),
    );
  };

  const showValidationErrors = () => {
    const missingNames = requiredFieldNames.filter(
      (name) => !isFilled(associationData[name], name),
    );

    const missing = missingNames
      .map((name) => {
        const field = formFields.find((f) => f.name === name);
        const label = field?.label || name;
        return `<li>${label}</li>`;
      })
      .join("");

    Swal.fire({
      title: "¡Campos incompletos!",
      icon: "error",
      html: `<div style="text-align: start;"><p>Por favor complete los siguientes campos:</p><ul style="margin-left:20px;">${missing}</ul></div>`,
      confirmButtonColor: "#1ab394",
    });
  };

  // --- Handlers ---
  const handleChange = (e) =>
    setAssociationData({
      ...associationData,
      [e.target.name]: e.target.value.trim(),
    });

  const handleAutocompleteChange = (_, value, field) =>
    setAssociationData((prev) => ({ ...prev, [field]: value }));

  const handleChangeCheckbox = () => {
    setMarketingToSend((prev) => (prev ? 0 : 1));
    setMarketingToShow((prev) => !prev);
  };

  const handleSubmit = async () => {
    if (!validateFields()) return showValidationErrors();

    const formData = new FormData();
    const payload = {
      id: associationId,
      company_id: associationData.associationCompany.id,
      asoc_name: associationData.associationName,
      asoc_grupo: associationData.associationGroups.id,
      asoc_notifications: associationData.associationNotifications.id,
      asoc_contactname: associationData.associationContactPerson,
      asoc_cif: associationData.associationCif,
      asoc_address: associationData.associationAddress,
      asoc_province: associationData.associationProvince.provincia,
      asoc_postalcode: associationData.associationPostalCode,
      asoc_email: associationData.associationEmail,
      asoc_acuerdo: associationData.associationAgreementSigned.id,
      asoc_phone: associationData.associationPhone,
      asoc_observaciones: associationObservations
        ? associationObservations.replace(/\n/g, "<br />")
        : "",
      marketing: marketingToSend ? 1 : 0,
    };

    Object.entries(payload).forEach(([k, v]) => formData.append(k, v));

    const confirm = await alertConfirm({
      title: "¿Desea guardar los cambios?",
      icon: "warning",
    });
    if (!confirm.isConfirmed)
      return Swal.fire("Cambios no guardados", "", "info");

    try {
      // Mostrar swal de carga
      Swal.fire({
        title: "Guardando...",
        html: "Por favor espere mientras se actualizan los datos.",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Enviar solicitud
      const res = await api.post(
        `${process.env.REACT_APP_API_URL}/associations/update`,
        formData,
      );

      // Cerrar el swal de carga
      Swal.close();

      if (res.data.error)
        return alertError(
          "¡No se guardaron los cambios!",
          "Inténtelo nuevamente.",
        );

      await Swal.fire({
        title: "¡Guardado!",
        icon: "success",
        confirmButtonText: "Ver Asociación",
        confirmButtonColor: "#1ab394",
      });

      navigate("/asociaciones/ver?id=" + associationId);
    } catch (error) {
      Swal.close(); // asegurarse de cerrar el swal si ocurre error
      console.error(error);
      alertError("Error", "Hubo un problema al guardar los cambios.");
    }
  };

  return (
    <div className="fatherContainer">
      <CommonSubHeader title="Asociaciones" subTitle="Editar" />

      <Fade in={!loading} timeout={800}>
        <Box component="form" className="formMainContainer" autoComplete="off">
          <Grid container textAlign="center" p={2} className="fontSize">
            {formFields.map((field, idx) => {
              if (field.type === "autocomplete") {
                return (
                  <Grid item xs={12} sm={6} key={idx} sx={{ p: "5px" }}>
                    <Autocomplete
                      value={associationData[field.name] || null}
                      onChange={(_, val) =>
                        handleAutocompleteChange(_, val, field.name)
                      }
                      size="small"
                      options={field.options}
                      loading={field.loading}
                      isOptionEqualToValue={field.isOptionEqualToValue}
                      getOptionLabel={field.getOptionLabel}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          size="small"
                          label={field.label}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>
                );
              }

              if (field.type === "textarea") {
                return (
                  <Grid item xs={12} key={idx} sx={{ p: "5px" }}>
                    <FormControl fullWidth>
                      <FormLabel sx={{ textAlign: "left" }}>
                        Observaciones
                      </FormLabel>
                      <textarea
                        value={associationObservations}
                        style={{
                          height: "100px",
                          resize: "none",
                          padding: "6px 13px",
                          fontSize: "15px",
                          borderRadius: "4px",
                          border: "1px solid rgba(128, 128, 128, 0.35)",
                        }}
                        onChange={(e) =>
                          setAssociationObservations(e.target.value)
                        }
                      />
                    </FormControl>
                  </Grid>
                );
              }

              return (
                <Grid item xs={12} sm={6} key={idx} sx={{ p: "5px" }}>
                  <TextField
                    name={field.name}
                    label={field.label}
                    value={associationData[field.name]}
                    onChange={handleChange}
                    size="small"
                    fullWidth
                    inputProps={{
                      maxLength:
                        field.name === "associationPostalCode" ? 10 : undefined,
                    }}
                  />
                </Grid>
              );
            })}

            {/* Checkbox marketing */}
            <Grid
              container
              p="5px"
              m="5px"
              sx={{ mt: "15px", borderRadius: "4px" }}>
              <Checkbox
                checked={marketingToShow && marketingToSend === 1}
                onClick={handleChangeCheckbox}
                color="success"
                sx={{
                  "& .MuiSvgIcon-root": { fontSize: 28, width: 28, height: 28 },
                }}
              />
              <Grid item xs={10} md={11} m="auto">
                <div
                  style={{
                    fontWeight: "700",
                    textAlign: "left",
                    color: "#676a6c",
                  }}>
                  Acepto el uso de mi correo electrónico para el envío de
                  comunicaciones con fines informativos y comerciales.
                </div>
              </Grid>
            </Grid>

            {/* Botones */}
            <Grid item xs={12}>
              <ButtonGroup
                sx={{
                  width: "100%",
                  justifyContent: "center",
                  textAlign: "center",
                  mt: 2,
                }}>
                <Button
                  sx={{ width: "100%" }}
                  onClick={handleSubmit}
                  variant="contained"
                  color="success">
                  Guardar
                </Button>
                <Button
                  sx={{ width: "100%" }}
                  onClick={() =>
                    goView("asociaciones", associationId, "asociación")
                  }
                  variant="outlined"
                  color="info">
                  Ver
                </Button>
                <Button
                  sx={{ width: "100%" }}
                  onClick={() => goTable("asociaciones", "Asociaciones")}
                  variant="outlined"
                  color="error">
                  Tabla
                </Button>
              </ButtonGroup>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    </div>
  );
};

export default Editar;
